import {
  ORGANIC_SHAPES,
  ORGANIC_SHAPE_COUNT,
  organicEyeColor,
} from "./organic.js";

export type OrganicPoint = { x: number; y: number };

export type OrganicDroplet = {
  center: OrganicPoint;
  radiusX: number;
  radiusY: number;
  angle: number;
  opacity: number;
};

export type OrganicFrame = {
  points: OrganicPoint[];
  color: string;
  eyeColor: string;
  eyeY: number;
  eyeGap: number;
  smile: number;
  blink: number;
  faceScale: number;
  look: OrganicPoint;
  scaleX: number;
  scaleY: number;
  rotation: number;
  floatY: number;
  shadowScale: number;
  stemScale: number;
  droplets: OrganicDroplet[];
};

class OrganicSpring {
  value: number;
  velocity = 0;

  constructor(value: number) {
    this.value = value;
  }

  step(target: number, dt: number, stiffness: number, damping: number) {
    this.velocity += ((target - this.value) * stiffness - this.velocity * damping) * dt;
    this.value += this.velocity * dt;
  }
}

function parseRgb(hex: string): [number, number, number] {
  const match = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.exec(hex);
  if (!match) return [255, 45, 121];
  let h = match[1]!;
  if (h.length === 3) h = [...h].map((c) => c + c).join("");
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

function mixColor(aHex: string, bHex: string, t: number): string {
  if (t <= 0) return aHex;
  if (t >= 1) return bHex;
  const a = parseRgb(aHex);
  const b = parseRgb(bHex);
  const r = Math.round(a[0] + (b[0] - a[0]) * t);
  const g = Math.round(a[1] + (b[1] - a[1]) * t);
  const bl = Math.round(a[2] + (b[2] - a[2]) * t);
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${bl.toString(16).padStart(2, "0")}`;
}

const SEED_POINTS: OrganicPoint[] = Array.from({ length: 56 }, (_, i) => {
  const angle = -Math.PI / 2 + (i / 56) * Math.PI * 2;
  return {
    x: 100 + Math.cos(angle) * 16,
    y: 100 + Math.sin(angle) * 16,
  };
});

/**
 * Deterministic physics and entrance animation simulation clock.
 * Faithfully matches iOS OrganicMotion.swift and ui/bot.html.
 */
export class OrganicMotion {
  shapeIndex: number;
  reducedMotion: boolean;

  private time = 0;
  private points: OrganicPoint[];
  private velocities: OrganicPoint[];
  private accumulator = 0;
  private introStart: number | null = null;
  private introImpulseApplied = false;
  private happyFrom = 0;
  private happyUntil = 0.95;
  private blinkStart: number | null = null;
  private nextBlink = 2.2;
  private randomSeed = 0xd12a;
  private focused = false;
  private textLength = 0;

  private bodyX = new OrganicSpring(1);
  private bodyY = new OrganicSpring(1);
  private rotation = new OrganicSpring(-11);
  private smile = new OrganicSpring(0);
  private stem = new OrganicSpring(0);

  private eyeY: number;
  private eyeGap: number;
  private look: OrganicPoint = { x: 0, y: 0 };

  private color: string;
  private eyeColor: string;
  private fromColor: string;
  private targetColor: string;
  private fromEyes: string;
  private colorStart = 0;

  constructor(shapeIndex: number, color: string, reducedMotion = false) {
    this.shapeIndex =
      ((shapeIndex % ORGANIC_SHAPE_COUNT) + ORGANIC_SHAPE_COUNT) % ORGANIC_SHAPE_COUNT;
    this.reducedMotion = reducedMotion;
    this.color = color;
    this.eyeColor = organicEyeColor(color);
    this.fromColor = color;
    this.targetColor = color;
    this.fromEyes = this.eyeColor;

    const shape = ORGANIC_SHAPES[this.shapeIndex]!;
    this.points = shape.points.map((p) => ({ x: p.x, y: p.y }));
    this.velocities = Array.from({ length: this.points.length }, () => ({ x: 0, y: 0 }));
    this.eyeY = shape.eyeY;
    this.eyeGap = shape.eyeGap;

    if (reducedMotion) {
      this.settle();
    } else {
      this.replay();
    }
  }

  get currentShape() {
    return ORGANIC_SHAPES[this.shapeIndex]!;
  }

  get frame(): OrganicFrame {
    if (this.reducedMotion) {
      const shape = this.currentShape;
      return {
        points: shape.points.map((p) => ({ x: p.x, y: p.y })),
        color: this.targetColor,
        eyeColor: organicEyeColor(this.targetColor),
        eyeY: shape.eyeY,
        eyeGap: shape.eyeGap,
        smile: 0,
        blink: 1,
        faceScale: 1,
        look: { x: 0, y: 0 },
        scaleX: 1,
        scaleY: 1,
        rotation: 0,
        floatY: 0,
        shadowScale: 1,
        stemScale: shape.hasStem ? 1 : 0,
        droplets: [],
      };
    }

    const elapsed = this.introStart !== null ? this.time - this.introStart : null;
    const isSeed = elapsed !== null && elapsed < 0.195;
    let expansion = 1;
    if (elapsed !== null) {
      if (isSeed) {
        expansion = 0.75 + (0.45 * elapsed) / 0.195;
      } else {
        const p = Math.min(1, (elapsed - 0.195) / 0.56);
        expansion = 1 - Math.exp(-6.2 * p) * Math.cos(10.5 * p) * 0.65;
      }
    }

    const pts = this.points.map((p) => ({
      x: 100 + (p.x - 100) * expansion,
      y: 100 + (p.y - 100) * expansion,
    }));

    let blink = 1.0;
    if (this.blinkStart !== null) {
      blink = Math.max(
        0.08,
        1 - Math.sin(Math.min(1, (this.time - this.blinkStart) / 0.145) * Math.PI) * 0.92,
      );
    }

    let faceScale = 1.0;
    let droplets: OrganicDroplet[] = [];
    if (elapsed !== null) {
      const p = Math.max(0, Math.min(1, (elapsed - 0.23) / 0.38));
      faceScale = elapsed < 0.23 ? 0 : 1 - Math.exp(-6.5 * p) * Math.cos(9.5 * p);
      droplets = OrganicMotion.calculateDroplets(elapsed);
    }

    const floatY = Math.sin(this.time * 2.2) * 3.8 + (this.focused ? 3.5 : 0);
    const shadowScale = Math.max(
      0.2,
      (1 - floatY * 0.018) * (isSeed ? 0.25 : expansion),
    );

    return {
      points: pts,
      color: this.color,
      eyeColor: this.eyeColor,
      eyeY: this.eyeY,
      eyeGap: this.eyeGap,
      smile: Math.max(0, Math.min(1, this.smile.value)),
      blink,
      faceScale,
      look: { ...this.look },
      scaleX: this.bodyX.value,
      scaleY: this.bodyY.value,
      rotation: this.rotation.value,
      floatY,
      shadowScale,
      stemScale: Math.max(0, this.stem.value),
      droplets,
    };
  }

  replay() {
    if (this.reducedMotion) {
      this.settle();
      return;
    }
    this.introStart = this.time;
    this.introImpulseApplied = false;
    this.accumulator = 0;
    this.points = SEED_POINTS.map((p) => ({ x: p.x, y: p.y }));
    this.velocities = Array.from({ length: this.points.length }, () => ({ x: 0, y: 0 }));
    this.bodyX = new OrganicSpring(1);
    this.bodyY = new OrganicSpring(1);
    this.rotation = new OrganicSpring(-11);
    this.smile = new OrganicSpring(0);
    this.stem = new OrganicSpring(0);
    this.blinkStart = null;
    this.nextBlink = this.time + 2.2;
    this.happyFrom = this.time;
    this.happyUntil = this.time + 0.95;
  }

  changeShape(nextIndex: number) {
    const valid =
      ((nextIndex % ORGANIC_SHAPE_COUNT) + ORGANIC_SHAPE_COUNT) % ORGANIC_SHAPE_COUNT;
    if (valid === this.shapeIndex) {
      this.poke();
      return;
    }
    this.shapeIndex = valid;
    if (this.reducedMotion) {
      this.settle();
      return;
    }
    const shape = ORGANIC_SHAPES[this.shapeIndex]!;
    for (let i = 0; i < this.points.length; i++) {
      const target = shape.points[i]!;
      const wave = Math.sin((i / this.points.length) * Math.PI * 4) * 3;
      this.velocities[i]!.x += (target.x - this.points[i]!.x) * 0.16 + wave;
      this.velocities[i]!.y += (target.y - this.points[i]!.y) * 0.16 - wave;
    }
    this.impulse(3, -3.3);
    this.blinkStart = this.time;
    this.happyFrom = this.time + 0.18;
    this.happyUntil = this.time + 0.98;
  }

  changeColor(nextColor: string) {
    if (nextColor === this.targetColor) return;
    this.fromColor = this.color;
    this.fromEyes = this.eyeColor;
    this.targetColor = nextColor;
    this.colorStart = this.time;
    if (this.reducedMotion) {
      this.settle();
      return;
    }
    this.impulse(-2.4, 2.6);
    this.happyFrom = this.time;
    this.happyUntil = this.time + 0.78;
  }

  poke() {
    if (this.reducedMotion) return;
    this.impulse(3.6, -3.9);
    this.happyFrom = this.time;
    this.happyUntil = this.time + 0.85;
  }

  setLook(focused: boolean, textLength: number) {
    this.focused = focused;
    this.textLength = textLength;
  }

  setReducedMotion(value: boolean) {
    if (value === this.reducedMotion) return;
    this.reducedMotion = value;
    this.settle();
    this.nextBlink = this.time + 2.2;
  }

  advance(delta: number) {
    if (!Number.isFinite(delta) || delta <= 0 || this.reducedMotion) return;
    const step = 1.0 / 120;
    this.accumulator += Math.min(delta, 0.1);
    while (this.accumulator + 1e-10 >= step) {
      this.accumulator = Math.max(0, this.accumulator - step);
      this.tick(step);
    }
  }

  private tick(dt: number) {
    this.time += dt;
    const elapsed = this.introStart !== null ? this.time - this.introStart : null;
    const isSeed = elapsed !== null && elapsed < 0.195;

    if (isSeed) {
      this.points = SEED_POINTS.map((p) => ({ x: p.x, y: p.y }));
      this.velocities = Array.from({ length: this.points.length }, () => ({ x: 0, y: 0 }));
    } else {
      if (this.introStart !== null && !this.introImpulseApplied) {
        this.introImpulseApplied = true;
        this.impulse(3.6, -3.9);
      }
      const shape = this.currentShape;
      for (let i = 0; i < this.points.length; i++) {
        const angle = -Math.PI / 2 + (i / this.points.length) * Math.PI * 2;
        const breathe =
          Math.sin(angle * 3 + this.time * 2.4) * 1.05 +
          Math.cos(angle * 2 - this.time * 1.7) * 0.75;
        const target = shape.points[i]!;
        this.velocities[i]!.x +=
          ((target.x + Math.cos(angle) * breathe - this.points[i]!.x) * 250 -
            this.velocities[i]!.x * 14.5) *
          dt;
        this.velocities[i]!.y +=
          ((target.y + Math.sin(angle) * breathe - this.points[i]!.y) * 250 -
            this.velocities[i]!.y * 14.5) *
          dt;
        this.points[i]!.x += this.velocities[i]!.x * dt;
        this.points[i]!.y += this.velocities[i]!.y * dt;
      }
    }

    if (elapsed !== null && elapsed >= 0.78) {
      this.introStart = null;
    }

    const breath = Math.sin(this.time * 2.2) * 0.016;
    this.bodyX.step(1 + breath, dt, 225, 11.8);
    this.bodyY.step(1 - breath, dt, 225, 11.8);

    const isHappy =
      this.time >= this.happyFrom && this.time < this.happyUntil && !this.focused;
    this.smile.step(isHappy ? 1 : 0, dt, 200, 15);
    this.rotation.step(isHappy ? -11 : 0, dt, 145, 11.5);
    this.stem.step(this.currentShape.hasStem && !isSeed ? 1 : 0, dt, 260, 13);

    const ease = 1 - Math.exp(-dt * 14);
    this.eyeY += (this.currentShape.eyeY - this.eyeY) * ease;
    this.eyeGap += (this.currentShape.eyeGap - this.eyeGap) * ease;
    this.look.x +=
      ((this.focused ? ((this.textLength % 9) - 4) * 1.4 : 0) - this.look.x) * ease;
    this.look.y += ((this.focused ? 6.5 : 0) - this.look.y) * ease;

    if (this.blinkStart !== null && this.time - this.blinkStart > 0.145) {
      this.blinkStart = null;
    }
    if (this.time >= this.nextBlink) {
      this.blinkStart = this.time;
      const nextSeed =
        (BigInt(Math.floor(this.randomSeed)) * 6364136223846793005n +
          1442695040888963407n) &
        0xffffffffn;
      this.randomSeed = Number(nextSeed);
      this.nextBlink = this.time + 2.5 + Math.abs(Math.sin(this.time)) * 2.6;
    }

    const p = Math.min(1, Math.max(0, (this.time - this.colorStart) / 0.24));
    const colorEase = 1 - (1 - p) ** 3;
    this.color = mixColor(this.fromColor, this.targetColor, colorEase);
    this.eyeColor = mixColor(
      this.fromEyes,
      organicEyeColor(this.targetColor),
      colorEase,
    );
  }

  private impulse(x: number, y: number) {
    this.bodyX.velocity = Math.max(-6, Math.min(6, this.bodyX.velocity + x));
    this.bodyY.velocity = Math.max(-6, Math.min(6, this.bodyY.velocity + y));
  }

  private settle() {
    this.introStart = null;
    this.accumulator = 0;
    const shape = this.currentShape;
    this.points = shape.points.map((p) => ({ x: p.x, y: p.y }));
    this.velocities = Array.from({ length: this.points.length }, () => ({ x: 0, y: 0 }));
    this.bodyX = new OrganicSpring(1);
    this.bodyY = new OrganicSpring(1);
    this.rotation = new OrganicSpring(0);
    this.smile = new OrganicSpring(0);
    this.stem = new OrganicSpring(shape.hasStem ? 1 : 0);
    this.eyeY = shape.eyeY;
    this.eyeGap = shape.eyeGap;
    this.look = { x: 0, y: 0 };
    this.blinkStart = null;
    this.happyUntil = this.time;
    this.color = this.targetColor;
    this.fromColor = this.color;
    this.eyeColor = organicEyeColor(this.color);
    this.fromEyes = this.eyeColor;
  }

  static calculateDroplets(elapsed: number): OrganicDroplet[] {
    const specs: [number, number, number, number, number, number][] = [
      [-0.75, 48, 6.5, 1, 0, 0.21],
      [2.85, 42, 5.5, 0.9, 0.018, 0.21],
      [0.95, 64, 5, 0.38, 0.032, 0.23],
      [-2.15, 54, 4.5, 0.75, 0.024, 0.22],
      [1.85, 50, 6, 0.85, 0.01, 0.215],
    ];

    const result: OrganicDroplet[] = [];
    for (const [angle, distance, radius, opacity, delay, duration] of specs) {
      const p = (elapsed - delay) / duration;
      if (p > 0 && p < 1) {
        const d = distance * (1 - p * p * p);
        const stretch = 1 + Math.sin(p * Math.PI) * 0.35;
        const scale = Math.sin(p * Math.PI * 0.85);
        result.push({
          center: {
            x: 100 + Math.cos(angle) * d,
            y: 100 + Math.sin(angle) * d,
          },
          radiusX: radius * scale * stretch,
          radiusY: (radius * scale) / Math.sqrt(stretch),
          angle,
          opacity,
        });
      }
    }
    return result;
  }
}
